import React, { useEffect, useState, useRef } from 'react';
import { Plus, Edit3, Trash2, Loader2, X, UploadCloud, Image as ImageIcon, Eye, EyeOff } from 'lucide-react';
import {
  adminListBanners, adminCreateBanner, adminUpdateBanner, adminDeleteBanner,
  adminUploadImage, backendUrl,
} from '../../lib/api';
import { toast } from '../../hooks/use-toast';

const BannerForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial || {
    tag: 'Featured',
    title: '',
    subtitle: '',
    cta_label: 'Explore',
    cta_href: '/corporate-gifting',
    image_url: '',
    order: 0,
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF({ ...f, [k]: v });

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUploadImage(file, 'banners');
      set('image_url', res.url);
      toast({ title: 'Image uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onSave(f); } finally { setSaving(false); }
  };

  const inputCls = 'w-full bg-white border border-[#EFE3D2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C58B76]';

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-2xl text-[#2C2416]">{initial ? 'Edit banner' : 'Add banner'}</h3>
          <button type="button" onClick={onCancel} className="p-1 hover:bg-[#FAF6F1] rounded-full"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Banner Image *</label>
            <div className="flex gap-3 mt-1">
              <input required className={inputCls} value={f.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="Paste URL or upload below" />
              <button type="button" onClick={() => fileRef.current?.click()} className="btn-outline whitespace-nowrap rounded-lg px-4 text-xs uppercase tracking-widest inline-flex items-center gap-2">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Upload
              </button>
              <input type="file" hidden ref={fileRef} accept="image/*" onChange={(e) => onUpload(e.target.files?.[0])} />
            </div>
            {f.image_url && (
              <div className="mt-2 h-32 bg-[#FAF6F1] rounded-lg overflow-hidden">
                <img src={backendUrl(f.image_url)} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Small Tag</label><input className={inputCls} value={f.tag} onChange={(e) => set('tag', e.target.value)} placeholder="e.g. Diwali 2026" /></div>
            <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Display Order</label><input type="number" className={inputCls} value={f.order} onChange={(e) => set('order', parseInt(e.target.value) || 0)} /></div>
          </div>

          <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Title *</label><input required className={inputCls} value={f.title} onChange={(e) => set('title', e.target.value)} placeholder="Big headline" /></div>
          <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Subtitle</label><textarea rows="2" className={inputCls} value={f.subtitle} onChange={(e) => set('subtitle', e.target.value)} placeholder="Supporting line" /></div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Button Text</label><input className={inputCls} value={f.cta_label} onChange={(e) => set('cta_label', e.target.value)} /></div>
            <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Button Link</label><input className={inputCls} value={f.cta_href} onChange={(e) => set('cta_href', e.target.value)} placeholder="/corporate-gifting" /></div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#2C2416]">
            <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Active (shown on homepage)
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} className="btn-outline rounded-full px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : 'Save banner'}
          </button>
        </div>
      </form>
    </div>
  );
};

const BannersTab = () => {
  const [banners, setBanners] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setBanners(await adminListBanners());
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing === 'new') { await adminCreateBanner(data); toast({ title: 'Banner added' }); }
      else { await adminUpdateBanner(editing.id, data); toast({ title: 'Banner updated' }); }
      setEditing(null); load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    await adminDeleteBanner(id); toast({ title: 'Deleted' }); load();
  };

  const toggle = async (b) => {
    await adminUpdateBanner(b.id, { ...b, active: !b.active });
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-[#5B4A3A]">{banners.length} banner{banners.length !== 1 && 's'} · shown on the homepage hero slider</p>
        <button onClick={() => setEditing('new')} className="btn-primary rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add banner
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banners.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-[#EFE3D2] p-10 text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-[#C58B76]" />
            <p className="mt-3 text-[#5B4A3A]">No banners yet. Default slides will be used until you add one.</p>
          </div>
        )}
        {banners.map(b => (
          <div key={b.id} className="bg-white rounded-2xl border border-[#EFE3D2] overflow-hidden group">
            <div className="aspect-[16/10] bg-[#FAF6F1] overflow-hidden">
              <img src={backendUrl(b.image_url)} alt={b.title} className={`w-full h-full object-cover ${b.active ? '' : 'opacity-40'}`} />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${b.active ? 'bg-[#C8D5B9] text-[#3A4A2B]' : 'bg-[#F4D5C5] text-[#7A5C4C]'}`}>{b.active ? 'Live' : 'Hidden'}</span>
                <span className="text-[10px] uppercase tracking-widest text-[#7A5C4C]">Order: {b.order}</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-[#C58B76]">{b.tag}</div>
              <div className="font-serif-display text-lg text-[#2C2416] mt-1 leading-snug line-clamp-2">{b.title}</div>
              <div className="flex justify-between mt-3">
                <div className="flex gap-3">
                  <button onClick={() => setEditing(b)} className="text-xs uppercase tracking-widest text-[#2C2416] hover:text-[#C58B76] inline-flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => toggle(b)} className="text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-[#C58B76] inline-flex items-center gap-1">
                    {b.active ? <><EyeOff className="w-3.5 h-3.5" /> Hide</> : <><Eye className="w-3.5 h-3.5" /> Show</>}
                  </button>
                </div>
                <button onClick={() => remove(b.id)} className="text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <BannerForm initial={editing === 'new' ? null : editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
};

export default BannersTab;
